import { sendGAEvent, sendGALinkClickEvent } from '@noths/polaris-client-google-analytics';
import type { RecommendedProduct } from '@noths/polaris-dev-ts-types';

import type { Placement } from 'src/constants/recommendations';
import { getPersonalisationBreakdown } from 'src/redux/personalisation/helpers/selectorHelpers';
import type { PersonalisationState } from 'src/redux/personalisation/types';
import { generateProductsForEvent } from 'src/utils/googleAnalytics';
import type {
  ProductClickEvent,
  ProductImpressionsInitialEvent,
  ProductShelfImpressionEvent,
  ProductShelfNavigationImpression,
} from './events';

interface TrackRecommendedProductsImpression {
  personalisation: PersonalisationState;
  placementStrategy: string;
  placementType: Placement;
  products: RecommendedProduct[];
}

export const trackRecommendedProductsImpression = ({
  personalisation,
  placementStrategy,
  placementType,
  products,
}: TrackRecommendedProductsImpression) => {
  sendGAEvent<ProductImpressionsInitialEvent>({
    ecommerce: null,
  });

  const eventLabel = `${placementStrategy || ''} : ${placementType}`;

  const { amountAdded: addedPersonalisationCost, amountRemoved: removedPersonalisationCost } =
    getPersonalisationBreakdown(personalisation.selectedPersonalisations, 'GBP');

  sendGAEvent<ProductShelfImpressionEvent>({
    event: 'product_impressions',
    event_category: 'Rich Relevance',
    event_action: 'Product Impressions',
    event_label: eventLabel,
    ecommerce: {
      impressions: generateProductsForEvent({
        products: products!,
        placementName: placementType,
        placementStrategy,
        personalisationsTotalAmount: addedPersonalisationCost - removedPersonalisationCost,
      }),
    },
  });
};

interface TrackRecommendedProductClick {
  personalisation: PersonalisationState;
  placementStrategy: string;
  placementType: Placement;
  product: RecommendedProduct;
  productIndex: number;
}
export const trackRecommendedProductClick = (
  e: React.MouseEvent,
  {
    personalisation,
    placementStrategy,
    placementType,
    product,
    productIndex,
  }: TrackRecommendedProductClick,
) => {
  sendGAEvent<ProductImpressionsInitialEvent>({
    ecommerce: null,
  });

  const positionInList = productIndex + 1;
  const eventLabel = `${positionInList} : ${placementStrategy || ''} : ${placementType}`;

  const { amountAdded: addedPersonalisationCost, amountRemoved: removedPersonalisationCost } =
    getPersonalisationBreakdown(personalisation.selectedPersonalisations, 'GBP');

  sendGALinkClickEvent<ProductClickEvent>(e, {
    event: 'product_clicks',
    event_category: 'Rich Relevance',
    event_action: 'Product Click',
    event_label: eventLabel,
    algonomy_tracking_URL: product.linkURL,
    ecommerce: {
      click: {
        actionField: {
          list: `product - rich relevance - ${placementType} - ${placementStrategy}`,
        },
        products: generateProductsForEvent({
          products: [product!],
          placementName: placementType,
          placementStrategy,
          positionInList,
          personalisationsTotalAmount: addedPersonalisationCost - removedPersonalisationCost,
        }),
      },
    },
  });
};

interface TrackRecommendedProductsCarouselNavigation {
  navigation: {
    direction: 'Left' | 'Right';
    type: 'Arrow Click' | 'Scroll';
  };
  placementStrategy: string;
  placementType: Placement;
}

export const trackRecommendedProductsCarouselNavigation = ({
  navigation,
  placementStrategy,
  placementType,
}: TrackRecommendedProductsCarouselNavigation) => {
  const eventLabel = `${placementStrategy} : ${placementType}`;
  const { direction, type } = navigation;

  const navigationType = type === 'Arrow Click' ? 'Arrow click' : 'Swipe';
  const navigationDirection = direction === 'Right' ? 'next' : 'previous';

  sendGAEvent<ProductShelfNavigationImpression>({
    event: 'custom_event',
    event_category: 'Rich Relevance',
    event_action: `${navigationType} ${navigationDirection}`,
    event_label: eventLabel,
  });
};
